import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, Router } from '@angular/router';
import { AppGlobalActions } from '@app/_store/actions/global.actions';
import { selectProfile } from '@app/_store/features/profile/profile.selectors';
import { AuthService } from '@core/services/auth.service';
import { Actions, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { filter, map, Observable, of, race, switchMap, take, tap } from 'rxjs';

export const AuthGuard: CanActivateFn | CanActivateChildFn = (
  route,
  state
): Observable<boolean> => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const store = inject(Store);
  const actions$ = inject(Actions);

  const sendToSignIn = (): boolean => {
    // Remember where the deep link pointed, query included; `afterLoginNavigate`
    // takes the user there instead of the dashboard (QA audit #2, A14). Only the
    // error interceptor wrote this key before, so a fresh visitor's link was lost.
    if (state.url && state.url !== '/') {
      sessionStorage.setItem('prev_rout', state.url);
    }
    void router.navigate(['/sign-in'], { queryParams: route.queryParams });
    return false;
  };

  return authService.isAuthenticated().pipe(
    switchMap((token) => {
      if (!token) {
        return of(sendToSignIn());
      }

      // R40. A `userInfo` gate used to sit here, and it was inverted: it fired a
      // `reset` refetch when userInfo was **already** in the store, and did
      // nothing when it was missing — where it returned `of({})`, which passed
      // its own truthiness filter anyway. So it never gated anything; it only
      // cost one wasted request per entry into the app area (measured: exactly
      // one `fetchData` for `{userInfo}` on every re-entry). It also read a live
      // `select` inside a `switchMap` that dispatched, which is a self-feeding
      // loop for as long as the guard stays subscribed.
      //
      // Deleted rather than corrected: the profile gate below is the real one,
      // and both branches of `loadAppDataIfNeeded` bring userInfo with them —
      // the cached branch hydrates every field from IndexedDB, the cold branch
      // loads every container. A profile without userInfo passed before this
      // change too, so nothing that used to get through is stopped now.
      const profileArrived$ = store.select(selectProfile).pipe(
        tap((profile) => {
          if (!profile) {
            store.dispatch(AppGlobalActions.loadAppDataIfNeeded());
          }
        }),
        filter((profile) => !!profile && Object.keys(profile).length > 0),
        take(1),
        map(() => true)
      );

      // R39. Waiting for the profile was the only way out of here, so a load
      // that failed — or one that came back without a profile — left this
      // observable silent forever. `asyncOrderGuard` awaits it, so the
      // navigation never finished and the user sat on a blank page with nothing
      // to retry from.
      //
      // Reducers run before an action reaches any listener, so after a
      // successful load the profile is already in the store and
      // `profileArrived$` wins this race. This side only fires when the load has
      // settled and there is still nothing to show.
      const loadSettledWithoutProfile$ = actions$.pipe(
        ofType(
          AppGlobalActions.loadAppDataSuccess,
          AppGlobalActions.loadAppDataFailure
        ),
        concatLatestFrom(() => store.select(selectProfile)),
        filter(([, profile]) => !profile || Object.keys(profile).length === 0),
        take(1),
        map(() => {
          console.error(
            'App data finished loading without a profile — returning to sign-in instead of leaving the app on a blank page'
          );
          return sendToSignIn();
        })
      );

      return race(profileArrived$, loadSettledWithoutProfile$);
    })
  );
};
